import {
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  Check,
  ClipboardList,
  Mail,
  PauseCircle,
  Signature,
  Printer,
  RotateCcw
} from "lucide-react";
import { useState, type FC, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

import { ClientPortalAPI } from "../api";
import { usePortalT } from "../i18n";
import {
  comingUp,
  explainerText,
  initials,
  isOverdue,
  isTaskReady,
  reminderLine,
  taskAction,
  taskMeta,
  taskPercent
} from "../portalModel";
import { clientPortalCasePath, clientPortalSignPath } from "../routes";
import type {
  PortalAppointment,
  PortalCase,
  PortalReminder,
  PortalStage,
  PortalTask,
  PortalTeamMember
} from "../types";

export const PortalCard: FC<{
  title?: string;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}> = ({ title, action, className, children }) => (
  <section
    className={cn(
      "flex flex-col gap-3 rounded-xl border border-border bg-background p-4 md:p-5",
      className
    )}
  >
    {title ? (
      <div className="flex items-center gap-2">
        <h2 className="text-base font-semibold">{title}</h2>
        <span className="flex-1" />
        {action}
      </div>
    ) : null}
    {children}
  </section>
);

export const Stepper: FC<{ stages: PortalStage[] }> = ({ stages }) => {
  const { t } = usePortalT();
  return (
    <ol
      className="grid"
      style={{
        gridTemplateColumns: `repeat(${stages.length}, minmax(0, 1fr))`
      }}
    >
      {stages.map((stage, i) => (
        <li
          key={stage.key}
          className="flex flex-col gap-1.5"
          aria-current={stage.state === "now" ? "step" : undefined}
        >
          <span className="flex items-center">
            <span
              className={cn(
                "flex size-5 shrink-0 items-center justify-center rounded-full",
                stage.state === "done" && "bg-success text-white",
                stage.state === "now" && "bg-primary text-primary-foreground",
                stage.state === "next" && "border border-line-3 bg-background"
              )}
            >
              {stage.state === "done" ? (
                <Check className="size-3" strokeWidth={3} />
              ) : null}
              {stage.state === "now" ? (
                <span className="size-1.5 rounded-full bg-primary-foreground" />
              ) : null}
            </span>
            {i < stages.length - 1 ? (
              <span
                className={cn(
                  "mx-1.5 h-0.5 flex-1",
                  stage.state === "done" ? "bg-success" : "bg-border"
                )}
              />
            ) : null}
          </span>
          <span
            className={cn(
              "pe-2 text-xs leading-tight md:text-xs",
              stage.state === "now"
                ? "font-semibold text-foreground"
                : "text-muted-foreground",
              stage.state !== "now" && "hidden sm:block"
            )}
          >
            {t(`stage.${stage.key}`)}
          </span>
        </li>
      ))}
    </ol>
  );
};

export const CaseSummary: FC<{ portalCase: PortalCase; link?: boolean }> = ({
  portalCase,
  link
}) => {
  const { t } = usePortalT();
  return (
    <div className="flex flex-col gap-3" data-testid="portal-case">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-sm font-semibold">{portalCase.title}</span>
        {portalCase.outcome === "approved" ? (
          <span className="text-xs font-medium text-success">
            {t("case.approved")}
          </span>
        ) : null}
        <span className="flex-1" />
        {link ? (
          <Button asChild variant="link" size="sm" className="h-auto p-0">
            <Link to={clientPortalCasePath(portalCase.id)}>
              {t("case.fullTimeline")}
              <ArrowRight className="size-3.5 rtl:rotate-180" />
            </Link>
          </Button>
        ) : null}
      </div>
      <div className="pt-1">
        <Stepper stages={portalCase.stages} />
      </div>
      {portalCase.on_hold ? (
        <div className="flex items-start gap-2.5 rounded-lg border border-border bg-fill-1 px-3.5 py-3 text-sm">
          <PauseCircle className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          {t("case.onHold")}
        </div>
      ) : (
        <div className="flex flex-col gap-1 rounded-lg border border-line-2 bg-fill-1 px-3.5 py-3">
          <span className="text-sm font-semibold">{t("case.now")}</span>
          <span className="text-sm">{explainerText(portalCase, t)}</span>
        </div>
      )}
    </div>
  );
};

/** Opens an intake in this tab via the portal, so the link never leaves it. */
const useOpenTask = () => {
  const navigate = useNavigate();
  const { t } = usePortalT();
  const [opening, setOpening] = useState<string | null>(null);
  const open = (task: PortalTask) => {
    if (task.kind !== "intake") {
      navigate(clientPortalSignPath(task.id));
      return;
    }
    setOpening(task.id);
    ClientPortalAPI.openTask(task.id)
      .then((path) => navigate(`${path}?from=portal`))
      .catch(() => toast.error(t("tasks.openFailed")))
      .finally(() => setOpening(null));
  };
  return { open, opening };
};

export const TaskRows: FC<{
  tasks: PortalTask[];
  preview: boolean;
}> = ({ tasks, preview }) => {
  const { t, i18n } = usePortalT();
  const { open, opening } = useOpenTask();
  const firstReady = tasks.find(isTaskReady)?.id;
  return (
    <ul className="flex flex-col">
      {tasks.map((task) => {
        const action = taskAction(task);
        const ready = isTaskReady(task);
        const primary = task.id === firstReady;
        const Icon = task.changes_requested
          ? RotateCcw
          : task.kind === "sign"
          ? Signature
          : task.kind === "ink"
          ? Printer
          : ClipboardList;
        // Previews may look at a signing page; only intakes are client-only.
        const blocked = !ready || (preview && task.kind === "intake");
        return (
          <li
            key={task.id}
            className="flex flex-col gap-3 border-t border-line-2 py-3.5 first:border-t-0 first:pt-1 sm:flex-row sm:items-center sm:gap-3.5"
            data-testid="portal-task"
            aria-disabled={ready ? undefined : true}
          >
            <span
              className={cn(
                "flex min-w-0 flex-1 items-start gap-3.5",
                !ready && "opacity-60"
              )}
            >
              <span
                className={cn(
                  "flex size-10 shrink-0 items-center justify-center rounded-xl",
                  task.changes_requested
                    ? "bg-warn-soft text-warn"
                    : primary
                    ? "bg-primary/10 text-primary"
                    : "bg-muted text-muted-foreground"
                )}
              >
                <Icon className="size-4.25" />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="text-md font-medium leading-snug">
                  {task.title}
                </span>
                {task.changes_requested ? (
                  <span className="text-sm text-warn">
                    {t(
                      task.kind === "intake"
                        ? "tasks.changesRequested"
                        : "tasks.sendAgain"
                    )}
                  </span>
                ) : null}
                {task.note ? (
                  <span className="text-sm italic text-muted-foreground">
                    “{task.note}”
                  </span>
                ) : null}
                <span
                  className={cn(
                    "text-sm text-muted-foreground",
                    isOverdue(task) && "text-destructive"
                  )}
                >
                  {taskMeta(task, t, i18n.language)}
                </span>
                {task.total > 0 ? (
                  <Progress
                    value={taskPercent(task)}
                    className="mt-0.5 h-1.5 max-w-65"
                    aria-label={t("tasks.progress", {
                      answered: task.answered,
                      total: task.total
                    })}
                  />
                ) : null}
              </span>
            </span>
            <Button
              variant={primary ? "default" : ready ? "outline" : "ghost"}
              className="w-full sm:w-auto"
              disabled={blocked || opening === task.id}
              title={
                preview && task.kind === "intake"
                  ? t("tasks.previewOnly")
                  : undefined
              }
              onClick={() => open(task)}
            >
              {opening === task.id ? t("tasks.opening") : t(`tasks.${action}`)}
            </Button>
          </li>
        );
      })}
    </ul>
  );
};

export const TeamList: FC<{ team: PortalTeamMember[] }> = ({ team }) => {
  const { t } = usePortalT();
  return (
    <ul className="flex flex-col gap-3">
      {team.map((m) => (
        <li key={m.uid} className="flex items-center gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
            {initials(m.name)}
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="text-sm font-semibold">{m.name}</span>
            <span className="truncate text-xs text-muted-foreground">
              {m.role}
              {m.email ? ` · ${m.email}` : ""}
            </span>
          </span>
          {m.email ? (
            <Button asChild size="icon-sm" variant="ghost">
              <a
                href={`mailto:${m.email}`}
                aria-label={t("team.email", { name: m.name })}
              >
                <Mail />
              </a>
            </Button>
          ) : null}
        </li>
      ))}
    </ul>
  );
};

export const ComingUpCard: FC<{
  appointments: PortalAppointment[];
  tasks: PortalTask[];
}> = ({ appointments, tasks }) => {
  const { t, i18n } = usePortalT();
  const items = comingUp(appointments, tasks, t, i18n.language);
  if (!items.length) return null;
  return (
    <PortalCard title={t("comingUp.title")}>
      <ul className="flex flex-col gap-3">
        {items.map((item) => (
          <li key={item.key} className="flex items-start gap-3">
            <span className="flex size-7.5 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              {item.kind === "appointment" ? (
                <CalendarClock className="size-3.5" />
              ) : (
                <ClipboardList className="size-3.5" />
              )}
            </span>
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="text-sm font-medium">{item.title}</span>
              <span className="text-xs text-muted-foreground">{item.when}</span>
            </span>
          </li>
        ))}
      </ul>
    </PortalCard>
  );
};

export const ReminderCards: FC<{ reminders: PortalReminder[] }> = ({
  reminders
}) => {
  const { t, i18n } = usePortalT();
  return (
    <>
      {reminders.map((r) => (
        <section
          key={`${r.kind}-${r.subject}`}
          className="flex items-start gap-2.5 rounded-xl border border-warn-line bg-warn-soft p-4"
          data-testid="portal-reminder"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warn" />
          <span className="flex flex-col gap-0.5">
            <span className="text-sm font-semibold text-warn">
              {reminderLine(r, t, i18n.language)}
            </span>
            <span className="text-sm">
              {t(r.expired ? "reminders.expiredHint" : "reminders.expiresHint")}
            </span>
          </span>
        </section>
      ))}
    </>
  );
};
